"use client";

import {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from "react";
import {
  GARAGE_STORAGE_KEY_CARS,
  GARAGE_STORAGE_KEY_SERVICES,
} from "@/lib/garageStorage";
import { USER_SESSION_LOGOUT_EVENT } from "@/lib/authSession";
import type { Car, ServiceRecord } from "@/types/garage";

const STORAGE_KEY_CARS = GARAGE_STORAGE_KEY_CARS;
const STORAGE_KEY_SERVICES = GARAGE_STORAGE_KEY_SERVICES;

function loadCars(): Car[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_CARS);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

function loadServices(): ServiceRecord[] {
  if (typeof window === "undefined") return [];
  try {
    const raw = localStorage.getItem(STORAGE_KEY_SERVICES);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

type GarageContextType = {
  cars: Car[];
  serviceRecords: ServiceRecord[];
  addCar: (car: Omit<Car, "id" | "addedAt">) => Car;
  /** Replace garage list (e.g. after loading from Petromin `GET /vehicles/{customerId}`). */
  overwriteCars: (next: Car[]) => void;
  /** Restore cars from `localStorage` (e.g. after sign-out). */
  reloadCarsFromStorage: () => void;
  updateCar: (id: string, updates: Partial<Car>) => void;
  removeCar: (id: string) => void;
  getCarById: (id: string) => Car | undefined;
  addServiceRecord: (record: Omit<ServiceRecord, "id">) => ServiceRecord;
  getServiceRecordsByCarId: (carId: string) => ServiceRecord[];
};

const GarageContext = createContext<GarageContextType | null>(null);

export function GarageProvider({ children }: { children: ReactNode }) {
  const [cars, setCars] = useState<Car[]>([]);
  const [serviceRecords, setServiceRecords] = useState<ServiceRecord[]>([]);
  const carsSaveSkipped = useRef(false);
  const servicesSaveSkipped = useRef(false);

  useEffect(() => {
    setCars(loadCars());
    setServiceRecords(loadServices());
  }, []);

  useEffect(() => {
    const onLogout = () => {
      setCars([]);
      setServiceRecords([]);
    };
    window.addEventListener(USER_SESSION_LOGOUT_EVENT, onLogout);
    return () =>
      window.removeEventListener(USER_SESSION_LOGOUT_EVENT, onLogout);
  }, []);

  useEffect(() => {
    if (!carsSaveSkipped.current) {
      carsSaveSkipped.current = true;
      return;
    }
    localStorage.setItem(STORAGE_KEY_CARS, JSON.stringify(cars));
  }, [cars]);

  useEffect(() => {
    if (!servicesSaveSkipped.current) {
      servicesSaveSkipped.current = true;
      return;
    }
    localStorage.setItem(STORAGE_KEY_SERVICES, JSON.stringify(serviceRecords));
  }, [serviceRecords]);

  const addCar = useCallback((car: Omit<Car, "id" | "addedAt">) => {
    const newCar: Car = {
      ...car,
      id: crypto.randomUUID(),
      addedAt: new Date().toISOString(),
    };
    setCars((prev) => [...prev, newCar]);
    return newCar;
  }, []);

  const overwriteCars = useCallback((next: Car[]) => {
    setCars(next);
  }, []);

  const reloadCarsFromStorage = useCallback(() => {
    setCars(loadCars());
  }, []);

  const updateCar = useCallback((id: string, updates: Partial<Car>) => {
    setCars((prev) =>
      prev.map((c) => (c.id === id ? { ...c, ...updates } : c))
    );
  }, []);

  const removeCar = useCallback((id: string) => {
    setCars((prev) => prev.filter((c) => c.id !== id));
    setServiceRecords((prev) => prev.filter((s) => s.carId !== id));
  }, []);

  const getCarById = useCallback(
    (id: string) => cars.find((c) => c.id === id),
    [cars]
  );

  const addServiceRecord = useCallback((record: Omit<ServiceRecord, "id">) => {
    const newRecord: ServiceRecord = {
      ...record,
      id: crypto.randomUUID(),
    };
    setServiceRecords((prev) => [...prev, newRecord]);
    return newRecord;
  }, []);

  const getServiceRecordsByCarId = useCallback(
    (carId: string) =>
      serviceRecords
        .filter((s) => s.carId === carId)
        .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()),
    [serviceRecords]
  );

  return (
    <GarageContext.Provider
      value={{
        cars,
        serviceRecords,
        addCar,
        overwriteCars,
        reloadCarsFromStorage,
        updateCar,
        removeCar,
        getCarById,
        addServiceRecord,
        getServiceRecordsByCarId,
      }}
    >
      {children}
    </GarageContext.Provider>
  );
}

export function useGarage() {
  const ctx = useContext(GarageContext);
  if (!ctx)
    throw new Error("useGarage must be used within GarageProvider");
  return ctx;
}
